import React, { useState, useEffect } from 'react';
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Filter, Calendar, Trash, Clock } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';

// Fallback bin IDs in case the API request fails
const AVAILABLE_BINS = ['R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R9', 'R13', 'R14', 'R15', 'R17', 'IST01', 'IST03', 'B1', 'B2', 'B3'];
const AVAILABLE_YEARS = ['2020', '2021', '2022', '2023', '2024', '2025'];

// Time periods for dropdown
const TIME_PERIODS = ['day', 'week', 'month', '3months', '6months', 'year'];

// Updated waste types to match Layout component
const WASTE_TYPES = ['organic', 'pmd', 'paper', 'residual'];

export function RecyclingHeader({
  selectedBins,
  setSelectedBins,
  selectedWasteType,
  setSelectedWasteType,
  selectedTimePeriod,
  setSelectedTimePeriod,
  year,
  setYear,
  allBins = [], 
  // Dropdown state control props
  binDropdownOpen: externalBinDropdownOpen,
  setBinDropdownOpen: externalSetBinDropdownOpen,
  yearDropdownOpen: externalYearDropdownOpen,
  setYearDropdownOpen: externalSetYearDropdownOpen,
  timePeriodDropdownOpen: externalTimePeriodDropdownOpen,
  setTimePeriodDropdownOpen: externalSetTimePeriodDropdownOpen,
  wasteTypeDropdownOpen: externalWasteTypeDropdownOpen,
  setWasteTypeDropdownOpen: externalSetWasteTypeDropdownOpen
}) {
  // Internal dropdown states
  const [internalBinDropdownOpen, setInternalBinDropdownOpen] = useState(false);
  const [internalYearDropdownOpen, setInternalYearDropdownOpen] = useState(false);
  const [internalTimePeriodDropdownOpen, setInternalTimePeriodDropdownOpen] = useState(false);
  const [internalWasteTypeDropdownOpen, setInternalWasteTypeDropdownOpen] = useState(false);
  const [availableBins, setAvailableBins] = useState([]);

  const { t } = useTranslation();

  // Helper function for time range translation
  const getTimeRangeTranslation = (timeRange) => {
    const translations =  {
      'day': t('timeRanges.Day'),
      'week': t('timeRanges.Week'),
      'month': t('timeRanges.Month'),
      '3months': t('timeRanges.3Month'),
      '6months': t('timeRanges.6Month'),
      'year': t('timeRanges.Year'),
      'Day': t('timeRanges.Day'),
      'Week': t('timeRanges.Week'),
      'Month': t('timeRanges.Month'),
      '3 Months': t('timeRanges.3Month'),
      '6 Months': t('timeRanges.6Month'),
      'Year': t('timeRanges.Year')
    };
    return translations[timeRange] || timeRange;
  };

  const getWasteTypeTranslation  = (wasteType) => {
    const translations =  {
      'organic':t('wasteImpact.streams.organic'),
      'pmd': t('wasteImpact.streams.pmd'),
      'paper': t('wasteImpact.streams.paper'),
      'residual': t('wasteImpact.streams.residual'),
    };
    return translations[wasteType] || wasteType;
  };

  const getTimePeriodDisplay = (period) => {
    return getTimeRangeTranslation(period);
  };

  // Fetch bin IDs dynamically
  useEffect(() => {
      const fetchBinIds = async () => {
          try {
              console.log("Layout: Fetching bin IDs from API");
              const response = await axiosInstance.get("bin_ids");
              const binIds = response.data || [];
              setAvailableBins(binIds);
          } catch (error) {
              console.error("Layout: Error fetching bin IDs:", error);
          }
      };
      fetchBinIds();
  }, []);

  // Determine open/close states (external vs internal)
  const binDropdownOpen = externalBinDropdownOpen !== undefined ? externalBinDropdownOpen : internalBinDropdownOpen;
  const setBinDropdownOpen = externalSetBinDropdownOpen || setInternalBinDropdownOpen;

  const yearDropdownOpen = externalYearDropdownOpen !== undefined ? externalYearDropdownOpen : internalYearDropdownOpen;
  const setYearDropdownOpen = externalSetYearDropdownOpen || setInternalYearDropdownOpen;

  const timePeriodDropdownOpen = externalTimePeriodDropdownOpen !== undefined ? externalTimePeriodDropdownOpen : internalTimePeriodDropdownOpen;
  const setTimePeriodDropdownOpen = externalSetTimePeriodDropdownOpen || setInternalTimePeriodDropdownOpen;

  const wasteTypeDropdownOpen = externalWasteTypeDropdownOpen !== undefined ? externalWasteTypeDropdownOpen : internalWasteTypeDropdownOpen;
  const setWasteTypeDropdownOpen = externalSetWasteTypeDropdownOpen || setInternalWasteTypeDropdownOpen;

  // --- SORTING LOGIC ---
  // 1. Get the raw list based on priority (Fetched -> Props -> Fallback)
  const rawBinOptions = availableBins.length > 0 ? availableBins : (allBins.length > 0 ? allBins : AVAILABLE_BINS);
   
  // 2. Sort the list alphanumerically (Natural Sort)
  // This ensures 'B2' comes before 'B10', and 'B' comes before 'R'
  const binOptions = [...rawBinOptions].sort((a, b) => {
    const valA = String(a || ''); // Safety check converting to string
    const valB = String(b || '');
    return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
  });

  // Handler functions
  const handleBinChange = (bin) => {
    setSelectedBins(prev =>
      prev.includes(bin)
        ? prev.filter(b => b !== bin)
        : [...prev, bin]
    );
  };

  const handleYearChange = (selectedYear) => {
    setYear(parseInt(selectedYear));
  };

  const handleWasteTypeChange = (wasteType) => {
    setSelectedWasteType(wasteType);
  };

  const handlePeriodChange = (period) => {
    setSelectedTimePeriod(period);
  };

  const handleOpenChange = (open, menuType) => {
    switch (menuType) {
      case 'bin':
        setBinDropdownOpen(open);
        break;
      case 'year':
        setYearDropdownOpen(open);
        break;
      case 'period':
        setTimePeriodDropdownOpen(open);
        break;
      case 'wasteType':
        setWasteTypeDropdownOpen(open);
        break;
    }
  };

  const selectAllBins = () => {
    setSelectedBins([...binOptions]);
  };

  const clearBins = () => {
    setSelectedBins([]);
  };

  return (
    <header className="group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear sticky top-0 backdrop-blur-md z-30 border-b w-full max-w-[100vw] overflow-x-hidden px-2 sm:px-4">
      <div className="flex w-full items-center justify-between gap-1">
        <div className="flex items-center min-w-0 max-w-[60%] sm:max-w-none">
          <SidebarTrigger className="-ml-1 h-8 w-8 sm:h-9 sm:w-9" />
          <Separator
            orientation="vertical"
            className="mx-1 sm:mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-sm sm:text-base font-medium truncate">{t('navigation.circularity')}</h1>
        </div>

        <div className="flex gap-1 sm:gap-2">
          {/* Year Filter */}
          <DropdownMenu
            open={yearDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'year')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.year')}: </span>
                {year}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-40"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel>{t('header.filters.selectYear')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {AVAILABLE_YEARS.map(y => (
                <DropdownMenuCheckboxItem
                  key={y}
                  checked={year.toString() === y}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleYearChange(y);
                  }}
                >
                  {y}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Bin Filter */}
          <DropdownMenu
            open={binDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'bin')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Trash className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.bins')}: </span>
                {selectedBins.length === binOptions.length
                  ? t('common.all')
                  : selectedBins.length === 0
                    ? t('common.clear')
                    : selectedBins.length === 1
                      ? selectedBins[0]
                      : `${selectedBins.length}`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel className="flex justify-between items-center">
                <span>{t('header.filters.selected')}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      selectAllBins();
                    }}
                  >
                    {t('common.all')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      clearBins();
                    }}
                  >
                    {t('common.clear')}
                  </Button>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* This map will now render the bins in sorted order */}
              {binOptions.map(bin => (
                <DropdownMenuCheckboxItem
                  key={bin}
                  checked={selectedBins.includes(bin)}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleBinChange(bin);
                  }}
                >
                  {bin}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Waste Type Filter */}
          <DropdownMenu
            open={wasteTypeDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'wasteType')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Filter className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.wasteTypes')}: </span>
                {getWasteTypeTranslation(selectedWasteType) || t('common.select')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-40"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel>{t('header.filters.selectWasteType')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {WASTE_TYPES.map(wasteType => (
                <DropdownMenuCheckboxItem
                  key={wasteType}
                  checked={selectedWasteType === wasteType}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleWasteTypeChange(wasteType);
                  }}
                >
                  {getWasteTypeTranslation(wasteType)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Time Period Filter */}
          <DropdownMenu
            open={timePeriodDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'period')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.period')}: </span>
                {selectedTimePeriod ? getTimePeriodDisplay(selectedTimePeriod) : t('common.select')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-40"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel>{t('header.filters.selectTimePeriod')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {TIME_PERIODS.map(period => (
                <DropdownMenuCheckboxItem
                  key={period}
                  checked={selectedTimePeriod === period}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePeriodChange(period);
                  }}
                >
                  {getTimePeriodDisplay(period)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}